import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palette, Pencil } from "lucide-react";

const EMPTY = { service_code: "", service_name: "", category: "DTF", unit_basis: "PCS", internal_allowed: true, external_allowed: true, default_process_steps: ["PRINTING"], notes: "" };
const PROD_STEPS = ["PRINTING", "PRESS", "FINISHING"];

export default function DpServices() {
  const { can } = useAuth();
  const canManage = can("digital_printing.service.manage");
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ categories: [], units: [] });
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/digital-printing/services").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => {
    api.get("/digital-printing/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
  }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (s) => { setEditId(s.id); setForm({ service_code: s.service_code, service_name: s.service_name, category: s.category, unit_basis: s.unit_basis, internal_allowed: s.internal_allowed, external_allowed: s.external_allowed, default_process_steps: s.default_process_steps || [], notes: s.notes || "" }); };

  const toggleStep = (st) => setForm((f) => {
    const has = (f.default_process_steps || []).includes(st);
    const next = has ? f.default_process_steps.filter((x) => x !== st) : PROD_STEPS.filter((x) => x === st || (f.default_process_steps || []).includes(x));
    return { ...f, default_process_steps: next };
  });

  const save = async () => {
    if (!form.service_name.trim()) return toast.error("Nama layanan wajib");
    try {
      if (editId) await api.put(`/digital-printing/services/${editId}`, form);
      else await api.post("/digital-printing/services", form);
      toast.success(editId ? "Layanan diperbarui" : "Layanan dibuat");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (s) => {
    try {
      await api.post(`/digital-printing/services/${s.id}/${s.active ? "deactivate" : "activate"}`);
      toast.success(s.active ? "Layanan dinonaktifkan" : "Layanan diaktifkan");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="dp-services">
      <PageHeader title="Layanan Digital Printing" subtitle="Master layanan Digital Printing. Gunakan Aktif/Nonaktif — layanan tidak dihapus permanen.">
        {canManage && <Button data-testid="dp-service-new" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500">+ Layanan</Button>}
      </PageHeader>

      {rows.length === 0 ? (
        <EmptyState title="Belum ada layanan" subtitle="Tambahkan layanan Digital Printing." icon={Palette} />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-slate-400">
              <th className="p-3">Kode</th><th>Nama Layanan</th><th>Kategori</th><th>Unit Basis</th><th>Internal</th><th>External</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} data-testid={`dp-service-row-${s.id}`} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{s.service_code}</td>
                  <td className="font-medium">{s.service_name}</td>
                  <td className="text-slate-500">{s.category}</td>
                  <td className="text-slate-500">{s.unit_basis}</td>
                  <td>{s.internal_allowed ? "✓" : "—"}</td>
                  <td>{s.external_allowed ? "✓" : "—"}</td>
                  <td><Badge variant="outline" className={s.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}>{s.active ? "Aktif" : "Nonaktif"}</Badge></td>
                  <td className="whitespace-nowrap text-right">
                    {canManage && <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`dp-service-edit-${s.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                    {canManage && <Button variant="ghost" size="sm" onClick={() => toggle(s)} data-testid={`dp-service-toggle-${s.id}`} className={s.active ? "text-rose-600" : "text-emerald-600"}>{s.active ? "Nonaktifkan" : "Aktifkan"}</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="dp-service-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Layanan" : "Layanan Baru"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs">Nama Layanan<Input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} data-testid="dp-service-name" /></label>
              <label className="text-xs">Kode (opsional)<Input value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} placeholder="auto dari nama" disabled={!!editId} /></label>
              <label className="text-xs">Kategori<select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-service-category">{meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
              <label className="text-xs">Unit Basis<select value={form.unit_basis} onChange={(e) => setForm({ ...form, unit_basis: e.target.value })} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-service-unit">{meta.units.map((u) => <option key={u} value={u}>{u}</option>)}</select></label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.internal_allowed} onChange={(e) => setForm({ ...form, internal_allowed: e.target.checked })} data-testid="dp-service-internal" /> Diizinkan Internal</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.external_allowed} onChange={(e) => setForm({ ...form, external_allowed: e.target.checked })} data-testid="dp-service-external" /> Diizinkan External</label>
              <label className="col-span-2 text-xs">Production Route (QC selalu wajib di akhir)
                <div className="mt-1 flex gap-3 rounded border p-2" data-testid="dp-service-route">
                  {PROD_STEPS.map((st) => (
                    <label key={st} className="flex items-center gap-1.5 text-xs font-normal">
                      <input type="checkbox" checked={(form.default_process_steps || []).includes(st)} onChange={() => toggleStep(st)} data-testid={`dp-route-${st}`} /> {st}
                    </label>
                  ))}
                  <span className="ml-auto text-[11px] text-slate-400">→ QC → COMPLETED</span>
                </div>
              </label>
              <label className="col-span-2 text-xs">Catatan<Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button onClick={save} data-testid="dp-service-save" className="bg-emerald-600 hover:bg-emerald-500">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
